import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  MapPin,
  PlusCircle,
  Eye,
  EyeOff,
  Play,
  Pause
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

interface HeroSectionProps {
  onExploreClick?: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ onExploreClick }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isFr = language === 'fr';

  const [cleanPhotoOnly, setCleanPhotoOnly] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);

  // Adaptive video source: ultra-light 960x540 mobile MP4 on phones, crisp desktop MP4 on laptops/desktops
  const [videoSrc, setVideoSrc] = useState<string>(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      return '/hero-background-video-mobile.mp4';
    }
    return '/hero-background-video.mp4';
  });

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      const target = isMobile ? '/hero-background-video-mobile.mp4' : '/hero-background-video.mp4';
      if (target !== videoSrc) {
        setVideoSrc(target);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [videoSrc]);

  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Mobile WebKit / Safari Autoplay & Continuous Playback Engine
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let isDisposed = false;

    // Direct DOM property enforcement required by iOS Safari & Android Chrome
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('x5-playsinline', 'true');
    video.setAttribute('autoplay', '');

    const playVideo = async () => {
      if (!video || isDisposed) return;
      try {
        video.defaultMuted = true;
        video.muted = true;
        video.playsInline = true;
        await video.play();
        if (!isDisposed) {
          setIsVideoPlaying(true);
        }
      } catch {
        // Autoplay can be deferred on mobile / iOS Low Power Mode until user interaction
        if (!isDisposed) {
          setIsVideoPlaying(!video.paused);
        }
      }
    };

    // If video source doesn't match current src, load it
    if (video.currentSrc && !video.currentSrc.endsWith(videoSrc)) {
      video.load();
    }

    playVideo();

    // Trigger play as soon as initial frames buffer
    const handleLoadedData = () => playVideo();
    const handleCanPlay = () => playVideo();
    const handlePlaying = () => {
      if (!isDisposed) setIsVideoPlaying(true);
    };
    const handlePause = () => {
      if (!isDisposed) setIsVideoPlaying(false);
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('pause', handlePause);

    // Fallback: iOS Low Power Mode (battery saver) blocks unprompted autoplay.
    // Any screen tap, touch, pointer, scroll, or keydown event unlocks playback!
    const unlockOnGesture = () => {
      if (!video || isDisposed) return;
      if (video.paused) {
        playVideo().then(() => {
          if (!video.paused) {
            detachGestureListeners();
          }
        });
      } else {
        detachGestureListeners();
      }
    };

    const gestureEvents = ['touchstart', 'touchend', 'pointerdown', 'pointerup', 'click', 'scroll', 'keydown'];
    const attachGestureListeners = () => {
      gestureEvents.forEach((evt) => {
        window.addEventListener(evt, unlockOnGesture, { passive: true });
      });
    };
    const detachGestureListeners = () => {
      gestureEvents.forEach((evt) => {
        window.removeEventListener(evt, unlockOnGesture);
      });
    };

    attachGestureListeners();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && video && video.paused) {
        playVideo();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isDisposed = true;
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('pause', handlePause);
      detachGestureListeners();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [videoSrc]);

  const toggleVideoPlayback = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.defaultMuted = true;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', 'true');
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsVideoPlaying(true);
          })
          .catch((err) => {
            console.warn('Playback blocked, reloading media:', err);
            video.load();
            video.play()
              .then(() => {
                setIsVideoPlaying(true);
              })
              .catch(() => {});
          });
      }
    } else {
      video.pause();
      setIsVideoPlaying(false);
    }
  };

  return (
    <section
      id="hero-section"
      className="relative w-full overflow-hidden bg-neutral-950 min-h-[500px] lg:min-h-[580px] flex items-center"
    >
      {/* Background Media Container */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Instant AVIF/WebP Static Poster for Zero-Wait Mobile LCP */}
        <picture className="absolute inset-0 w-full h-full pointer-events-none select-none">
          <source srcSet="/mount-cameroon-muyuka.avif" type="image/avif" />
          <source srcSet="/mount-cameroon-muyuka.webp" type="image/webp" />
          <img
            src="/mount-cameroon-fako.jpg"
            alt={isFr ? 'Panorama du Mont Cameroun et immobilier au Cameroun' : 'Mount Cameroon and real estate marketplace in Cameroon'}
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-center"
          />
        </picture>

        {/* Continuous Autoplaying Background Video */}
        <video
          ref={(node) => {
            videoRef.current = node;
            if (node) {
              node.defaultMuted = true;
              node.muted = true;
              node.playsInline = true;
            }
          }}
          src={videoSrc}
          poster="/mount-cameroon-fako.jpg"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
          onPlay={() => setIsVideoPlaying(true)}
          onPause={() => setIsVideoPlaying(false)}
          {...({
            'webkit-playsinline': 'true',
            'x5-playsinline': 'true',
            'x5-video-player-type': 'h5',
            'playsinline': 'true',
          })}
        />

        {/* Clean Neutral Scrim Overlay for Typographic Legibility */}
        {!cleanPhotoOnly && (
          <div
            className="absolute inset-0 pointer-events-none transition-opacity duration-300 z-1"
            style={{
              background:
                'linear-gradient(to right, rgba(9, 13, 22, 0.78) 0%, rgba(9, 13, 22, 0.52) 45%, rgba(9, 13, 22, 0.22) 78%, rgba(9, 13, 22, 0.32) 100%)',
            }}
          />
        )}
      </div>

      {/* Hero Content Container */}
      <div
        className={`relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20 sm:pt-12 sm:pb-24 lg:pt-14 lg:pb-28 w-full transition-opacity duration-300 ${
          cleanPhotoOnly ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        <div className="max-w-3xl">
          {/* Cadastral Trust Pill */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-black/40 border border-white/20 backdrop-blur-md text-xs font-semibold text-white/95 mb-4 shadow-xs">
            <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
            <span>
              {isFr
                ? 'Immobilier & Terrains Vérifiés • Cameroun'
                : 'Verified Land & Real Estate • Cameroon'}
            </span>
          </div>

          {/* Main Headline - Natural, Human Editorial Typography */}
          <h1 className="text-3xl sm:text-5xl lg:text-[52px] font-extrabold text-white leading-[1.12] tracking-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.75)]">
            {isFr
              ? 'Trouvez un terrain, une maison ou une location au Cameroun'
              : 'Find Land, Homes & Rentals Across Cameroon'}
          </h1>

          {/* Subtitle directing attention straight to the search bar below */}
          <p className="mt-3.5 text-sm sm:text-base lg:text-[17px] text-white/90 font-normal max-w-2xl leading-relaxed drop-shadow-[0_1px_6px_rgba(0,0,0,0.7)]">
            {isFr
              ? 'Annonces gratuites en direct avec les propriétaires, notaires et géomètres agréés à Douala, Yaoundé, Kribi, Buea et dans les 10 régions.'
              : 'Free property classifieds directly from landowners, sworn notaries, and licensed agents in Douala, Yaoundé, Kribi, Buea, and all 10 regions.'}
          </p>
        </div>
      </div>

      {/* Subtle Media Controls Positioned cleanly above the Search Bar Overlap */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-8 z-20 flex items-center gap-2">
        <button
          id="hero-image-raw-toggle"
          type="button"
          onClick={() => setCleanPhotoOnly(!cleanPhotoOnly)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium backdrop-blur-md transition-all cursor-pointer shadow-xs ${
            cleanPhotoOnly
              ? 'bg-white text-slate-900 border-white font-semibold'
              : 'bg-black/45 hover:bg-black/65 text-white/90 border-white/20'
          }`}
          aria-label={
            cleanPhotoOnly
              ? isFr
                ? 'Restaurer la Vue'
                : 'Restore View'
              : isFr
              ? 'Vue Dégagée'
              : 'Clear View'
          }
          title={
            cleanPhotoOnly
              ? isFr
                ? 'Restaurer la Vue'
                : 'Restore View'
              : isFr
              ? 'Vue Dégagée'
              : 'Clear View'
          }
        >
          {cleanPhotoOnly ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">
            {cleanPhotoOnly
              ? isFr
                ? 'Restaurer'
                : 'Restore'
              : isFr
              ? 'Vue dégagée'
              : 'Clear view'}
          </span>
        </button>

        <button
          id="hero-video-play-toggle"
          type="button"
          onClick={toggleVideoPlayback}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-black/45 hover:bg-black/65 text-white/90 border-white/20 text-xs font-medium backdrop-blur-md transition-all cursor-pointer shadow-xs"
          aria-label={
            isVideoPlaying
              ? isFr
                ? 'Pause'
                : 'Pause'
              : isFr
              ? 'Lecture'
              : 'Play'
          }
          title={
            isVideoPlaying
              ? isFr
                ? 'Pause'
                : 'Pause'
              : isFr
              ? 'Lecture'
              : 'Play'
          }
        >
          {isVideoPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 fill-white" />}
          <span className="hidden sm:inline">
            {isVideoPlaying
              ? isFr
                ? 'Pause'
                : 'Pause'
              : isFr
              ? 'Vidéo'
              : 'Play'}
          </span>
        </button>
      </div>
    </section>
  );
};
